/**
 * ReadinessBoardTable — trainee rows: score ring, state chip, 7-day trend
 * arrow, coverage label. Rows sorted at-risk-first by the API.
 */
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

export const STATE_META = {
  floor_ready: { label: 'Floor ready', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  on_track:    { label: 'On track',    cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  at_risk:     { label: 'At risk',     cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  not_ready:   { label: 'Not ready',   cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  not_scored:  { label: 'Not scored',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const RING_COLOR = {
  floor_ready: '#10b981', on_track: '#0ea5e9', at_risk: '#f59e0b',
  not_ready: '#f43f5e', not_scored: '#94a3b8',
};

export function StateChip({ state, count }) {
  const m = STATE_META[state] || STATE_META.not_scored;
  return (
    <Badge variant="outline" className={`${m.cls} font-medium`} data-testid={`state-chip-${state}`}>
      {m.label}{count != null ? ` · ${count}` : ''}
    </Badge>
  );
}

export function ScoreRing({ score, state, size = 44 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const pct = score != null ? Math.max(0, Math.min(100, score)) : 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        {score != null && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke={RING_COLOR[state] || RING_COLOR.not_scored} strokeWidth="4"
                  strokeLinecap="round" strokeDasharray={c}
                  strokeDashoffset={c * (1 - pct / 100)}
                  style={{ transition: 'stroke-dashoffset 400ms ease' }} />
        )}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold">
        {score != null ? Math.round(score) : '—'}
      </span>
    </div>
  );
}

function TrendArrow({ trend }) {
  if (trend == null) return <Minus className="w-4 h-4 text-muted-foreground/50" data-testid="trend-flat" />;
  if (trend > 0) return (
    <span className="inline-flex items-center text-emerald-600 text-xs font-medium" data-testid="trend-up">
      <ArrowUpRight className="w-4 h-4" />{trend}
    </span>
  );
  return (
    <span className="inline-flex items-center text-rose-600 text-xs font-medium" data-testid="trend-down">
      <ArrowDownRight className="w-4 h-4" />{trend}
    </span>
  );
}

export function ReadinessBoardTable({ rows, onSelect }) {
  if (!rows?.length) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="readiness-no-trainees">
        No active trainees in this batch.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid="readiness-board-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-4 py-2.5 font-medium">Trainee</th>
            <th className="px-4 py-2.5 font-medium">Score</th>
            <th className="px-4 py-2.5 font-medium">State</th>
            <th className="px-4 py-2.5 font-medium">7-day trend</th>
            <th className="px-4 py-2.5 font-medium">Coverage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.trainee_id}
                className="border-b last:border-0 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => onSelect(t)}
                data-testid={`readiness-row-${t.trainee_id}`}>
              <td className="px-4 py-2.5">
                <p className="font-medium">{t.name}</p>
                {t.employee_id && <p className="text-xs text-muted-foreground">{t.employee_id}</p>}
              </td>
              <td className="px-4 py-2.5"><ScoreRing score={t.score} state={t.state} /></td>
              <td className="px-4 py-2.5">
                <StateChip state={t.state} />
                {t.escalated && (
                  <p className="text-[10px] text-amber-600 mt-0.5">time escalation</p>
                )}
              </td>
              <td className="px-4 py-2.5"><TrendArrow trend={t.trend_7d} /></td>
              <td className="px-4 py-2.5">
                <span className="text-xs text-muted-foreground" data-testid={`coverage-${t.trainee_id}`}>
                  {t.coverage.label}
                </span>
                {t.coverage.missing.length > 0 && (
                  <Badge variant="outline" className="ml-2 text-[10px] bg-slate-50">Partial</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
